/**
 * Parser Hook — thin React wrapper around PapaParse.
 *
 * Responsibilities:
 *   1. File ingestion (text decoding + BOM stripping).
 *   2. PapaParse configuration (header row, quoted fields, dynamic typing OFF
 *      — we coerce types ourselves in the Validation Engine).
 *   3. Surface parse-time fatal errors separately from row-level issues.
 *
 * Anything beyond raw text → row[] lives elsewhere (mapping, validation).
 */
import { useCallback, useState } from "react";
import Papa from "papaparse";
import type { ParseResult, RawRow } from "./types";

interface ParserState {
  parsing: boolean;
  result: ParseResult | null;
  fileName: string | null;
}

const EMPTY: ParserState = { parsing: false, result: null, fileName: null };

/** Strip wrapping quotes a user may have copy-pasted around a header. */
const normalizeHeader = (h: string): string => (h || "").trim().replace(/^"+|"+$/g, "");

/**
 * Read the file as text first so we control the encoding (Excel exports
 * frequently have a UTF-8 BOM which Papa otherwise leaves on the first
 * header string).
 */
const readAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || "").replace(/^\uFEFF/, ""));
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsText(file, "utf-8");
  });

export function useCsvParser() {
  const [state, setState] = useState<ParserState>(EMPTY);

  const reset = useCallback(() => setState(EMPTY), []);

  const parseFile = useCallback(async (file: File): Promise<ParseResult> => {
    setState({ parsing: true, result: null, fileName: file.name });
    let result: ParseResult;
    try {
      const text = await readAsText(file);
      const parsed = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: "greedy",
        dynamicTyping: false,
        transformHeader: normalizeHeader,
      });
      const headers = (parsed.meta.fields || []).map(normalizeHeader);
      const fatalErrors = (parsed.errors || [])
        .filter((e) => e.type === "Delimiter" || e.type === "Quotes")
        .map((e) => `${e.type}: ${e.message}`);
      // Coerce every cell to string so the validator has a stable contract.
      const rows: RawRow[] = (parsed.data || []).map((r) => {
        const out: RawRow = {};
        for (const h of headers) out[h] = String(r[h] ?? "").trim();
        return out;
      });
      result = { headers, rows, fatalErrors };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      result = { headers: [], rows: [], fatalErrors: [msg] };
    }
    setState({ parsing: false, result, fileName: file.name });
    return result;
  }, []);

  return {
    parsing: state.parsing,
    result: state.result,
    fileName: state.fileName,
    parseFile,
    reset,
  };
}
